import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Mail, PenLine, Star, Users } from 'lucide-react';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { Button, buttonVariants } from '../../components/ui';
import Badge from '../../components/ui/Badge';
import { useTheme } from '../../contexts/ThemeContext';
import { formatDateTime } from '../../config/appConfig';
import { cn } from '../../lib/utils';
import {
  useSignature,
  useCanManageEmailSignatures,
} from './useData';
import { useAuth } from '../../contexts/AuthContext';
import {
  moduleConfig,
  canUserEditSignature,
} from './config';
import SignaturePreview from './SignaturePreview';
import { prepareSignatureHtml } from '../emails/utils/signatureUtils';

const MetaCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ title, icon, children, className }) => {
  const { colors } = useTheme();
  return (
    <section className={cn('rounded-xl border p-4', colors.border, colors.surface, className)}>
      <div className="mb-3 flex items-center gap-2">
        <span className={cn('flex h-8 w-8 items-center justify-center rounded-lg bg-violet-500/10 text-violet-600 dark:text-violet-400')}>
          {icon}
        </span>
        <h2 className={cn('text-sm font-semibold', colors.text)}>{title}</h2>
      </div>
      {children}
    </section>
  );
};

const View: React.FC = () => {
  const { colors } = useTheme();
  const { user: currentUser } = useAuth();
  const canManage = useCanManageEmailSignatures();
  const { id } = useParams<{ id: string }>();
  const sigId = id ? parseInt(id, 10) : null;
  const { data: signature, isLoading } = useSignature(sigId);

  const canEdit = signature != null && canUserEditSignature(signature, currentUser?.id);
  const preparedBody = prepareSignatureHtml(signature?.body || '');

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <LoadingSpinner size="large" message="Loading signature…" />
      </div>
    );
  }

  if (signature == null) {
    return (
      <div className="w-full px-4 py-6 sm:px-6">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
          Signature not found or you may not have access.
        </div>
        <div className="mt-4">
          <Link
            to={moduleConfig.module.route.list}
            className={cn(buttonVariants({ variant: 'outline' }), colors.surface, colors.text, colors.border)}
          >
            Back to signatures
          </Link>
        </div>
      </div>
    );
  }

  const userCount = signature.assignedUsers?.length ?? 0;
  const accountCount = signature.assignedAccounts?.length ?? 0;

  return (
    <div className={cn('flex h-full min-h-0 flex-col', colors.background)}>
      <div className="flex w-full min-h-0 flex-1 flex-col px-4 py-4 sm:px-6 sm:py-6">
        <Link
          to={moduleConfig.module.route.list}
          className={cn(
            'mb-4 inline-flex items-center gap-1.5 text-sm font-medium transition-colors',
            colors.textMuted,
            colors.interactiveHover,
          )}
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to signatures
        </Link>

        <div
          className={cn(
            'mb-6 flex flex-col gap-4 rounded-xl border px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5',
            colors.border,
            colors.surface,
          )}
        >
          <div className="flex min-w-0 items-start gap-4">
            <span
              className={cn(
                'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
                'bg-gradient-to-br from-violet-500/15 to-indigo-500/10 text-violet-600 dark:text-violet-400',
              )}
            >
              <PenLine className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className={cn('text-xl font-semibold tracking-tight sm:text-2xl', colors.text)}>
                  {signature.name}
                </h1>
                {signature.isDefault && (
                  <Badge variant="secondary" className="gap-1 rounded-full">
                    <Star className="h-3 w-3 fill-current text-amber-500" aria-hidden />
                    Default
                  </Badge>
                )}
                {signature.isViewOnly && (
                  <Badge variant="secondary" className="rounded-full">
                    View only
                  </Badge>
                )}
              </div>
              <div className={cn('mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs', colors.textMuted)}>
                {signature.updatedAt ? (
                  <span>Updated {formatDateTime(signature.updatedAt)}</span>
                ) : null}
                {signature.ownerName ? <span>Created by {signature.ownerName}</span> : null}
              </div>
            </div>
          </div>
          {canEdit ? (
            <Link to={moduleConfig.module.route.edit(signature.id)} className="shrink-0">
              <Button type="button" className="w-full gap-2 sm:w-auto">
                <PenLine className="h-4 w-4" aria-hidden />
                Edit signature
              </Button>
            </Link>
          ) : null}
        </div>

        <div className="grid min-h-0 flex-1 gap-6 xl:grid-cols-[minmax(280px,320px)_minmax(0,1fr)]">
          <aside className="space-y-4 xl:sticky xl:top-4 xl:self-start">
            <MetaCard title="Overview" icon={<PenLine className="h-4 w-4" aria-hidden />}>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className={cn('text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>Name</dt>
                  <dd className={cn('mt-0.5 font-medium', colors.text)}>{signature.name}</dd>
                </div>
                <div>
                  <dt className={cn('text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>Default</dt>
                  <dd className="mt-0.5">
                    <Badge variant={signature.isDefault ? 'success' : 'secondary'} className="rounded-md">
                      {signature.isDefault ? 'Yes — auto-insert in compose' : 'No'}
                    </Badge>
                  </dd>
                </div>
              </dl>
            </MetaCard>

            {canManage ? (
              <MetaCard
                title={`Assigned users (${userCount})`}
                icon={<Users className="h-4 w-4" aria-hidden />}
              >
                {userCount === 0 ? (
                  <p className={cn('text-sm', colors.textMuted)}>No users assigned</p>
                ) : (
                  <ul className="space-y-2">
                    {signature.assignedUsers.map((user) => (
                      <li
                        key={user.userId}
                        className={cn(
                          'flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm',
                          colors.border,
                          colors.surfaceHover,
                        )}
                      >
                        <span className={cn('min-w-0 truncate font-medium', colors.text)}>{user.name}</span>
                        {user.isDefault ? (
                          <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-amber-600 dark:text-amber-400">
                            Default
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </MetaCard>
            ) : null}

            <MetaCard
              title={`Email accounts (${accountCount})`}
              icon={<Mail className="h-4 w-4" aria-hidden />}
            >
              {accountCount === 0 ? (
                <p className={cn('text-sm', colors.textMuted)}>No accounts linked</p>
              ) : (
                <ul className="space-y-2">
                  {signature.assignedAccounts.map((account) => (
                    <li
                      key={account.emailAccountId}
                      className={cn(
                        'rounded-lg border px-3 py-2 text-sm',
                        colors.border,
                        colors.surfaceHover,
                      )}
                    >
                      <p className={cn('font-medium truncate', colors.text)}>{account.label}</p>
                      {account.emailAddress && account.emailAddress !== account.label ? (
                        <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)}>{account.emailAddress}</p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </MetaCard>

            {signature.isViewOnly ? (
              <p
                className={cn(
                  'rounded-xl border px-4 py-3 text-sm leading-relaxed',
                  colors.border,
                  'border-amber-500/30 bg-amber-500/5 text-amber-900 dark:text-amber-200',
                )}
              >
                Shared with multiple users — only the owner can edit this signature.
              </p>
            ) : null}
          </aside>

          <div className="min-w-0">
            <SignaturePreview
              html={preparedBody}
              className="h-full"
              emptyMessage="This signature has no content yet."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default View;
